import React, { useCallback, useEffect, useRef } from 'react';
import { Flex } from '@banhala/ui-kit';
import { GoodsInfoWebView } from './components/GoodsInfoWebView';
import { ContentContainer } from '../ContentContainer';
import { GoodsAtomicRenderer } from './components/AtomicRenderer';
import { RelatedOutfits } from './components/RelatedOutfits';
import { AiProduct } from './components/AiProduct';
import { RelatedTags } from './components/RelatedTags';
import { LegalInfo } from './components/LegalInfo';
import {
  useElementViewportTracking,
  useMountWhenNearViewport,
} from '../../../../store/sectionVisibility';
import { BeautyBanner } from '../../../BeautyBanner';
import { useGoodsDetail } from '../../../../queries/useGoodsDetail';
import { isNotNil } from 'shared/src/utils';
import { useGoodsInstanceId } from '../../../../hooks/useGoodsInstanceId';
import {
  retainedState,
  useRetainedState,
} from 'shared/src/hooks/retainedState';

const BEAUTY_BANNER_HEIGHT = 50;

// 섹션별 측정 높이 집계를 visit 단위로 유지한다. remount 시 추정 기본값으로 리셋됐다가
// 각 섹션 재측정으로 다시 채워지며 탭 총높이가 튀는(=바깥 리스트 높이/스크롤 점프) 것을
// 막아, 복원된 스크롤 offset 과 정합을 유지한다. (folded/coverIndex 와 동일한 취지)
const heightSlot = retainedState(() => ({
  info: 1556,
  atomic: 794,
  legal: 152,
  codi: 0,
  tags: 0,
  aiProduct: 0,
}));

export const GoodsInfoTab: React.FC<{
  focused: boolean;
  onChangeTabHeight: (height: number) => void;
}> = props => {
  const onChangeTabHeightRef = useRef(props.onChangeTabHeight);
  onChangeTabHeightRef.current = props.onChangeTabHeight;
  const key = useGoodsInstanceId('atomic');

  const trackingRef = useElementViewportTracking(key, 0);

  // 무거운 섹션(atomic/codi)은 뷰포트에 곧 진입할 때만 mount(지연 mount).
  // mount 전엔 추정 높이 placeholder 로 자리를 잡아 스크롤/레이아웃 점프를 막는다.
  const [atomicMountRef, atomicMounted] = useMountWhenNearViewport(
    'goods-info-tab/atomic',
  );
  const [codiMountRef, codiMounted] = useMountWhenNearViewport(
    'goods-info-tab/codi',
  );

  const { data: goodsDetail } = useGoodsDetail();
  const showBeautyBanner = isNotNil(
    goodsDetail?.component_visibility.beauty_component?.banner,
  );

  const [height, setHeight] = useRetainedState(heightSlot);

  useEffect(() => {
    const nextHeight =
      Object.values<number>(height).reduce((prev, acc) => prev + acc, 0) +
      (showBeautyBanner ? BEAUTY_BANNER_HEIGHT : 0);
    onChangeTabHeightRef.current(nextHeight);
  }, [height, showBeautyBanner]);

  const handleInfoHeight = useCallback(
    (h: number) => {
      setHeight(prev => {
        if (prev.info === h) return prev;
        return { ...prev, info: h };
      });
    },
    [setHeight],
  );

  const handleAtomicHeight = useCallback(
    (h: number) => {
      setHeight(prev => {
        if (prev.atomic === h) return prev;
        return { ...prev, atomic: h };
      });
    },
    [setHeight],
  );

  const handleCodiHeight = useCallback(
    (h: number) => {
      setHeight(prev => {
        if (prev.codi === h) return prev;
        return { ...prev, codi: h };
      });
    },
    [setHeight],
  );

  const handleTagsHeight = useCallback(
    (h: number) => {
      setHeight(prev => {
        if (prev.tags === h) return prev;
        return { ...prev, tags: h };
      });
    },
    [setHeight],
  );

  const handleAiProductHeight = useCallback(
    (h: number) => {
      setHeight(prev => {
        if (prev.aiProduct === h) return prev;
        return { ...prev, aiProduct: h };
      });
    },
    [setHeight],
  );

  const handleLegalHeight = useCallback(
    (h: number) => {
      setHeight(prev => {
        if (prev.legal === h) return prev;
        return { ...prev, legal: h };
      });
    },
    [setHeight],
  );

  return (
    <ContentContainer focused={props.focused}>
      <Flex>
        <GoodsInfoWebView onChangeHeight={handleInfoHeight} />
        {showBeautyBanner && <BeautyBanner />}
        {/* atomic: 뷰포트 근접 시 mount. mount 전엔 추정 높이로 자리 유지.
            DISPLAY 로깅용 trackingRef 는 실제 콘텐츠(mount 후)에 부착. */}
        <Flex
          ref={atomicMountRef}
          height={atomicMounted ? undefined : height.atomic}
        >
          {props.focused && atomicMounted && (
            <Flex ref={trackingRef}>
              <GoodsAtomicRenderer
                screen={'GOODS_DETAIL'}
                focused={props.focused}
                onChangeHeight={handleAtomicHeight}
              />
            </Flex>
          )}
        </Flex>
        {/* codi: 뷰포트 근접 시 mount */}
        <Flex ref={codiMountRef} height={codiMounted ? undefined : height.codi}>
          {codiMounted && (
            <RelatedOutfits
              focused={props.focused}
              onChangeHeight={handleCodiHeight}
            />
          )}
        </Flex>
        {/* 네이티브 additional 순서: 관련룩 → AI상품 → 관련태그 → 약관 */}
        <AiProduct onChangeHeight={handleAiProductHeight} />
        <RelatedTags onChangeHeight={handleTagsHeight} />
        <LegalInfo onChangeHeight={handleLegalHeight} />
      </Flex>
    </ContentContainer>
  );
};
